import React from "react";
import styled from "styled-components";
import { Text } from "@lmn/design-system";
import { OfferRoomDetail } from "@/lib/Types/OfferRoomDetail";
import { formatPriceWithDecimals } from "@/lib/Helpers/formatPrice";
import { indexRoomNames } from "@/lib/Helpers/indexRoomNames";

const PriceListContainer = styled.ul<{ $noBorder?: boolean }>(
  ({ theme, $noBorder }) => ({
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: theme.kallisto.space.space100,
    paddingBottom: $noBorder ? 0 : theme.kallisto.space.space100,
    ...(!$noBorder && {
      borderBottom: `1px solid ${theme.kallisto.color.border.static.borderNeutral}`,
    }),
  }),
);

const RoomHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: theme.kallisto.space.space100,
  marginBottom: theme.kallisto.space.space50,
  "& > span:first-child": {
    flex: 1,
    minWidth: 0,
    wordBreak: "break-word",
    maxWidth: "70%",
  },
  "& > span:last-child": {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
}));

const TravellerList = styled.ul(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStyle: "none",
  li: {
    display: "flex",
    gap: theme.kallisto.space.space100,
    justifyContent: "space-between",
    alignItems: "center",
    color: theme.kallisto.color.text.static.textSecondary,
  },
}));

function getTravellerType(age: number | null): string {
  if (age === null || age >= 18) return "Erwachsener";
  if (age < 2) return "Baby";
  return "Kind";
}

interface TravellerPriceListProps {
  offerRoomDetails: OfferRoomDetail[];
  noBorder?: boolean;
}

const TravellerPriceList: React.FC<TravellerPriceListProps> = ({
  offerRoomDetails,
  noBorder,
}) => {
  if (!offerRoomDetails || offerRoomDetails.length === 0) {
    return null;
  }

  const hasMultipleRooms = offerRoomDetails.length > 1;

  return (
    <PriceListContainer $noBorder={noBorder}>
      {offerRoomDetails.map((room, roomIndex) => (
        <li key={`room_${roomIndex}`}>
          <RoomHeader>
            <Text variant="BodySmall" forceTag="span" weight="bold">
              {hasMultipleRooms
                ? indexRoomNames(roomIndex, `s Zimmer - ${room.nameOffer}`)
                : room.nameOffer}
            </Text>
            {room.totalAmount > 0 && (
              <Text variant="BodySmall" forceTag="span" weight="bold">
                {formatPriceWithDecimals(room.totalAmount)}
              </Text>
            )}
          </RoomHeader>
          {room.personList && room.personList.length > 0 && (
            <TravellerList>
              {room.personList.map((person, personIndex) => (
                <Text
                  variant="BodySmall"
                  forceTag="li"
                  key={`person_${personIndex}`}
                >
                  {getTravellerType(person.age)}
                  <span>{formatPriceWithDecimals(person.price || 0)}</span>
                </Text>
              ))}
            </TravellerList>
          )}
        </li>
      ))}
    </PriceListContainer>
  );
};

export default TravellerPriceList;
